import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
// Icons removed - text-only UI

// ============================================================================
// LEGAL TEAMS DATABASE - Tier 1-2 (Early Game)
// ============================================================================

export const LEGAL_TEAMS = {
  "pacific-northwest": {
    id: "pacific-northwest",
    name: "Pacific Northwest Legal Group",
    class: "REGIONAL CONNECTORS",
    monthlyRetainer: 35000,
    tier: 1,
    strengths: [
      "PDX/SEA/BOI/SMF 20-30% fee reductions",
      "Knows airport staff personally",
      "Sometimes get first pick on slot times",
    ],
    weaknesses: [
      "FAA matters require subcontractor (+$50k)",
      "No connections outside PNW",
      "No international experience",
    ],
    bestFor: "Regional carriers, PNW-focused growth",
    // Mechanical effects
    effects: {
      feeReduction: { PDX: 0.25, SEA: 0.25, BOI: 0.20, SMF: 0.30, KPDX: 0.25, KSEA: 0.25, KBOI: 0.20, KSMF: 0.30 },
      defaultFeeReduction: 0.05,
      slotTimePriority: 0.15, // 15% chance of first pick
      faaSubcontractorCost: 50000,
      canReopenContracts: false,
      competitorIntel: false,
    },
  },
  "budget-air-legal": {
    id: "budget-air-legal",
    name: "Budget Air Legal",
    class: "BUDGET OPTION",
    monthlyRetainer: 12000,
    tier: 1,
    strengths: [
      "Cheapest option available",
      "Basic contract review included",
      "Warm body at negotiations",
    ],
    weaknesses: [
      "5-8% fee reductions max",
      "No FAA assistance",
      "No airport relationships",
      "Airports know you're running cheap",
    ],
    bestFor: "Startups watching every dollar",
    effects: {
      feeReduction: {},
      defaultFeeReduction: 0.065, // 5-8% average
      slotTimePriority: 0,
      faaSubcontractorCost: null, // Can't help with FAA
      canReopenContracts: false,
      competitorIntel: false,
      reputationPenalty: -0.05, // Airports judge you
    },
  },
  "kirkland-aviation": {
    id: "kirkland-aviation",
    name: "Kirkland Aviation Law",
    class: "DEALMAKERS",
    monthlyRetainer: 55000,
    tier: 2,
    strengths: [
      "25-35% gate fee reductions",
      "Negotiate shorter minimum commitments",
      "Competitor intel on slot usage",
      "Can reopen locked contracts",
    ],
    weaknesses: [
      "Standard FAA assistance only",
      "Transactional, not personal relationships",
      "Some airports find them aggressive",
    ],
    bestFor: "Aggressive expansion, cost optimization",
    effects: {
      feeReduction: {},
      defaultFeeReduction: 0.30, // 25-35% average
      slotTimePriority: 0.05,
      minimumReduction: 0.25, // 25% shorter minimums
      faaSubcontractorCost: 25000, // Standard rate
      canReopenContracts: true,
      competitorIntel: true,
      aggressiveReputation: true, // Some airports don't like them
    },
  },
};

// ============================================================================
// LEGAL TEAM SELECTION COMPONENT
// ============================================================================

export default function LegalTeamSelection({
  airportCode,
  airportName,
  onSelect,
  onSkip,
  worldState,
  availableTeams = Object.keys(LEGAL_TEAMS),
}) {
  const currentFirm = worldState?.legal?.currentFirm;
  const relationships = worldState?.legal?.relationships || {};
  const playerCash = worldState?.airline?.cash || 0;

  // Filter teams by availability and relationship status
  const teamsToShow = availableTeams
    .map(id => LEGAL_TEAMS[id])
    .filter(team => {
      const rel = relationships[team.id];
      // Don't show burned firms
      if (rel === "burned") return false;
      return true;
    });

  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount}`;
  };

  const getRelationshipBadge = (teamId) => {
    const rel = relationships[teamId];
    if (currentFirm?.id === teamId) {
      return <Badge className="bg-green-600">CURRENT FIRM</Badge>;
    }
    if (rel === "former") {
      return <Badge variant="outline" className="text-yellow-500 border-yellow-500">FORMER CLIENT</Badge>;
    }
    if (rel === "burned") {
      return <Badge variant="destructive">BURNED BRIDGE</Badge>;
    }
    return null;
  };

  const canAfford = (team) => {
    // Need at least 3 months retainer to hire
    return playerCash >= team.monthlyRetainer * 3;
  };

  const getClassColor = (className) => {
    switch (className) {
      case "REGIONAL CONNECTORS": return "text-blue-400";
      case "BUDGET OPTION": return "text-gray-400";
      case "DEALMAKERS": return "text-amber-400";
      default: return "text-gray-300";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-white">Legal Representation</h2>
            <p className="text-gray-400 text-sm">Slot Negotiation - {airportName || airportCode}</p>
          </div>

          {/* Advisory Message */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <p className="text-amber-200 font-medium">
              ! {airportName || airportCode} has reached out regarding slot negotiations.
            </p>
            <p className="text-amber-200/70 text-sm mt-1">
              Before proceeding, you may want to consider legal representation.
              A good aviation law firm can secure better rates, favorable terms, and protect your interests.
            </p>
          </div>

          {/* Current Legal Status */}
          <div className="mt-4 p-3 bg-gray-800 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Current Legal Representation:</span>
              {currentFirm ? (
                <div>
                  <span className="text-green-400 font-medium">{currentFirm.name}</span>
                  <span className="text-gray-500 ml-2">({formatCurrency(currentFirm.monthlyRetainer)}/mo)</span>
                </div>
              ) : (
                <span className="text-red-400 font-medium">NONE</span>
              )}
            </div>
          </div>
        </div>

        {/* Legal Teams List */}
        <div className="p-6 space-y-4">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide">Available Legal Teams</h3>

          {teamsToShow.map((team) => (
            <Card key={team.id} className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-lg text-white">{team.name}</CardTitle>
                      {getRelationshipBadge(team.id)}
                    </div>
                    <CardDescription className={`font-semibold ${getClassColor(team.class)}`}>
                      {team.class}
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold">
                      {formatCurrency(team.monthlyRetainer)}
                      <span className="text-gray-400 font-normal text-sm">/month</span>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {/* Strengths */}
                  <div>
                    <h4 className="text-xs font-medium text-green-400 uppercase tracking-wide mb-2">Strengths</h4>
                    <ul className="space-y-1">
                      {team.strengths.map((strength, i) => (
                        <li key={i} className="text-sm text-gray-300">
                          <span className="text-green-500">✓</span> {strength}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Weaknesses */}
                  <div>
                    <h4 className="text-xs font-medium text-red-400 uppercase tracking-wide mb-2">Weaknesses</h4>
                    <ul className="space-y-1">
                      {team.weaknesses.map((weakness, i) => (
                        <li key={i} className="text-sm text-gray-300">
                          <span className="text-red-500">✗</span> {weakness}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Best For */}
                <div className="bg-gray-900/50 rounded-lg p-3 mb-4">
                  <span className="text-xs font-medium text-blue-400 uppercase">Best For:</span>
                  <span className="text-sm text-gray-300 ml-2">{team.bestFor}</span>
                </div>

                {/* Hire Button */}
                <div className="flex justify-end">
                  {currentFirm?.id === team.id ? (
                    <Button disabled className="bg-green-600/20 text-green-400 border border-green-600">
                      Currently Retained
                    </Button>
                  ) : canAfford(team) ? (
                    <Button
                      onClick={() => onSelect(team)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Hire {team.name.split(" ")[0]}
                    </Button>
                  ) : (
                    <Button disabled className="bg-gray-700 text-gray-500">
                      Need {formatCurrency(team.monthlyRetainer * 3)} to hire
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-700 bg-gray-800/50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              {currentFirm ? (
                <span>Proceeding with <span className="text-green-400">{currentFirm.name}</span></span>
              ) : (
                <span className="text-amber-400">No legal representation - negotiations may be less favorable</span>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onSkip}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                {currentFirm ? "Continue with Current Firm" : "Skip - Negotiate Without Legal"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// HELPER FUNCTIONS FOR LEGAL SYSTEM
// ============================================================================

/**
 * Initialize legal state in worldState
 */
export function createLegalState() {
  return {
    currentFirm: null,
    relationships: {}, // firmId -> 'available' | 'active' | 'burned' | 'former'
    hireDate: null,
    monthlyRate: 0,
    totalPaid: 0,
    contractsNegotiated: 0,
  };
}

/**
 * Hire a legal team
 */
export function hireLegalTeam(legalState, team, gameTime) {
  const newState = { ...legalState };

  // If switching firms, mark old one as 'former'
  if (newState.currentFirm && newState.currentFirm.id !== team.id) {
    newState.relationships = {
      ...newState.relationships,
      [newState.currentFirm.id]: "former",
    };
  }

  // Set new firm
  newState.currentFirm = team;
  newState.relationships = {
    ...newState.relationships,
    [team.id]: "active",
  };
  newState.hireDate = gameTime;
  newState.monthlyRate = team.monthlyRetainer;

  return newState;
}

/**
 * Fire/release a legal team
 */
export function fireLegalTeam(legalState, burnBridge = false) {
  if (!legalState.currentFirm) return legalState;

  const newState = { ...legalState };
  const firmId = newState.currentFirm.id;

  newState.relationships = {
    ...newState.relationships,
    [firmId]: burnBridge ? "burned" : "former",
  };

  newState.currentFirm = null;
  newState.monthlyRate = 0;

  return newState;
}

/**
 * Calculate fee reduction for a specific airport
 */
export function calculateFeeReduction(legalState, airportCode) {
  if (!legalState?.currentFirm) return 0;

  const effects = legalState.currentFirm.effects;
  if (!effects) return 0;

  // Check for airport-specific reduction
  const specificReduction = effects.feeReduction?.[airportCode] ||
                           effects.feeReduction?.[airportCode.toUpperCase()];

  if (specificReduction) return specificReduction;

  // Fall back to default reduction
  return effects.defaultFeeReduction || 0;
}

/**
 * Check if legal team can help with FAA matters
 */
export function canHandleFAA(legalState) {
  if (!legalState?.currentFirm) return { canHandle: false, cost: null };

  const effects = legalState.currentFirm.effects;
  if (effects.faaSubcontractorCost === null) {
    return { canHandle: false, cost: null };
  }

  return {
    canHandle: true,
    cost: effects.faaSubcontractorCost,
    requiresSubcontractor: effects.faaSubcontractorCost > 0,
  };
}

/**
 * Process monthly legal billing
 */
export function processLegalBilling(legalState, currentCash) {
  if (!legalState?.currentFirm) return { newCash: currentCash, billed: 0 };

  const billed = legalState.monthlyRate;
  const newCash = currentCash - billed;

  return {
    newCash,
    billed,
    newTotalPaid: (legalState.totalPaid || 0) + billed,
  };
}
